import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import purchaseRequestService from "../../services/purchaseRequestService";
import toast from "react-hot-toast";
import {
  LayoutGrid,
  List,
  BarChart3,
  Eye,
  Download,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  ExternalLink,
} from "lucide-react";
import PurchaseReceipt from "./PurchaseReceipt";
import useNotificationRefresh from "../../hooks/useNotificationRefresh";

const ManagerApproval = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentManager, setCurrentManager] = useState(null);
  const [viewMode, setViewMode] = useState("kanban"); // 'kanban', 'list', 'dashboard'
  const [collapsedColumns, setCollapsedColumns] = useState({}); // Track collapsed state for each priority
  const [approvalData, setApprovalData] = useState({
    action: "",
    comments: "",
  });
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchPendingApprovals();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await purchaseRequestService.getSettings();
      setSettings(response.data.data?.settings || {});
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      // Now gets ALL pending approvals, not filtered by manager
      const response = await purchaseRequestService.getPendingApprovals({
        limit: 50,
      });
      console.log("All pending approvals:", response.data);
      setPendingRequests(response.data.data.requests || []);
      setCurrentManager(response.data.data.currentManager || null);
    } catch (error) {
      console.error("Fetch pending approvals error:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch pending approvals",
      );
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh when a procurement/approval notification arrives via WebSocket
  useNotificationRefresh(
    ["purchase_request_approval", "manager_action_required"],
    fetchPendingApprovals,
  );

  const handleApproval = async (requestId, action, comments = "") => {
    try {
      setProcessingId(requestId);

      const data = {
        action: action,
        comments: comments,
        stage: "approval_stage",
      };

      await purchaseRequestService.processApproval(requestId, data);

      toast.success(
        `Request ${action === "approve" ? "approved" : "rejected"} successfully`,
      );

      // Refresh the list
      fetchPendingApprovals();

      // Close modals
      setShowApprovalModal(false);
      setShowViewModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error("Approval error:", error);
      toast.error(
        error.response?.data?.message || `Failed to ${action} request`,
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadReceipt = (request) => {
    setSelectedRequest(request);
    setShowReceipt(true);
  };

  const getThreshold = () => {
    return settings.procurement_threshold || 1000000;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getNextStage = (amount) => {
    const threshold = getThreshold();
    return parseFloat(amount) >= threshold
      ? "Procurement Review"
      : "Finance Approval";
  };

  // Check if current user is the assigned manager for a request
  const isAssignedManager = (request) => {
    if (!currentManager || !request.approving_manager_id) return false;
    return currentManager.id === request.approving_manager_id;
  };

  // Group requests by priority for Kanban view
  const groupByPriority = () => {
    const groups = {
      urgent: [],
      high: [],
      medium: [],
      low: [],
    };

    pendingRequests.forEach((request) => {
      const priority = request.priority || "medium";
      if (groups[priority]) {
        groups[priority].push(request);
      }
    });

    return groups;
  };

  // Calculate dashboard statistics
  const getDashboardStats = () => {
    const totalValue = pendingRequests.reduce(
      (sum, req) => sum + parseFloat(req.amount),
      0,
    );
    const myRequests = pendingRequests.filter((req) => isAssignedManager(req));
    const myValue = myRequests.reduce(
      (sum, req) => sum + parseFloat(req.amount),
      0,
    );
    const requireProcurement = pendingRequests.filter(
      (req) => parseFloat(req.amount) >= getThreshold(),
    ).length;

    const byDepartment = {};
    pendingRequests.forEach((req) => {
      const dept = req.department || "Unknown";
      if (!byDepartment[dept]) {
        byDepartment[dept] = { count: 0, value: 0 };
      }
      byDepartment[dept].count++;
      byDepartment[dept].value += parseFloat(req.amount);
    });

    return {
      total: pendingRequests.length,
      totalValue,
      myRequests: myRequests.length,
      myValue,
      requireProcurement,
      byDepartment,
    };
  };

  const renderActionButtons = (request) => {
    const canApprove = isAssignedManager(request);

    if (!canApprove) {
      return (
        <div className="text-sm text-gray-500 italic">
          Assigned to: {request.ApprovingManager?.name || "Another manager"}
        </div>
      );
    }

    return (
      <div className="flex space-x-3">
        <button
          onClick={() => {
            setSelectedRequest(request);
            setApprovalData({ action: "reject", comments: "" });
            setShowApprovalModal(true);
          }}
          disabled={processingId === request.request_id}
          className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
        >
          {processingId === request.request_id ? "Processing..." : "Reject"}
        </button>

        <button
          onClick={() => {
            setSelectedRequest(request);
            setApprovalData({ action: "approve", comments: "" });
            setShowApprovalModal(true);
          }}
          disabled={processingId === request.request_id}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
        >
          {processingId === request.request_id ? "Processing..." : "Approve"}
        </button>
      </div>
    );
  };

  const toggleColumnCollapse = (priority) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [priority]: !prev[priority],
    }));
  };

  const renderKanbanView = () => {
    const groups = groupByPriority();
    const priorityConfig = {
      urgent: {
        label: "Urgent",
        color: "bg-red-50",
        headerColor: "bg-gradient-to-r from-red-500 to-red-600",
        borderColor: "border-red-200",
      },
      high: {
        label: "High",
        color: "bg-orange-50",
        headerColor: "bg-gradient-to-r from-orange-500 to-orange-600",
        borderColor: "border-orange-200",
      },
      medium: {
        label: "Medium",
        color: "bg-yellow-50",
        headerColor: "bg-gradient-to-r from-yellow-500 to-yellow-600",
        borderColor: "border-yellow-200",
      },
      low: {
        label: "Low",
        color: "bg-green-50",
        headerColor: "bg-gradient-to-r from-green-500 to-green-600",
        borderColor: "border-green-200",
      },
    };

    return (
      <div className="flex gap-4 pb-4">
        {Object.entries(priorityConfig).map(([priority, config]) => {
          const isCollapsed = collapsedColumns[priority];

          return (
            <div
              key={priority}
              className={`transition-all duration-300 ease-in-out ${
                isCollapsed ? "w-16 flex-shrink-0" : "flex-1"
              } ${config.color} border-2 ${config.borderColor} rounded-xl shadow-sm overflow-hidden flex flex-col min-h-[calc(100vh-350px)]`}
            >
              {/* Column Header */}
              <div
                className={`${config.headerColor} text-white ${isCollapsed ? "px-2 py-3" : "px-4 py-3"} transition-all duration-300 flex-shrink-0`}
              >
                {isCollapsed ? (
                  <div className="flex flex-col items-center space-y-2">
                    <button
                      onClick={() => toggleColumnCollapse(priority)}
                      className="p-1 hover:bg-white/20 rounded transition-colors"
                    >
                      <ChevronDown className="h-4 w-4 rotate-90" />
                    </button>
                    <div className="flex flex-col items-center">
                      <span className="bg-white/20 backdrop-blur-sm px-1.5 py-1 rounded-full text-xs font-bold mb-2">
                        {groups[priority].length}
                      </span>
                      <div
                        className="text-xs font-semibold whitespace-nowrap"
                        style={{
                          writingMode: "vertical-rl",
                          textOrientation: "mixed",
                        }}
                      >
                        {config.label}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-sm">
                      {config.label} Priority
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                        {groups[priority].length}
                      </span>
                      <button
                        onClick={() => toggleColumnCollapse(priority)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                      >
                        <ChevronUp className="h-4 w-4 -rotate-90" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Column Content */}
              <div
                className={`transition-all duration-300 ease-in-out flex-1 ${isCollapsed ? "opacity-0 h-0 overflow-hidden p-0" : "opacity-100 p-3 space-y-3"}`}
              >
                {groups[priority].map((request) => (
                  <div
                    key={request.id}
                    className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-3 hover:shadow-lg hover:border-blue-300 transition-all"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                        {request.request_id}
                      </div>
                      {isAssignedManager(request) && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-lg font-bold">
                          ✓ Yours
                        </span>
                      )}
                    </div>

                    {/* Amount - Prominent */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-2 mb-2 text-white shadow-md">
                      <p className="text-xs text-green-100 mb-0.5">Amount</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(request.amount)}
                      </p>
                    </div>

                    {/* Info */}
                    <div className="space-y-1.5 mb-2 text-xs">
                      <div>
                        <p className="text-gray-500">Requester</p>
                        <p className="font-semibold text-gray-900">
                          {request.requester_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Department</p>
                        <p className="font-semibold text-gray-900">
                          {purchaseRequestService.formatDepartment(
                            request.department,
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Item Description */}
                    <div className="bg-gray-50 rounded-lg p-2 mb-2 border border-gray-200">
                      <p className="text-xs text-gray-700 line-clamp-2">
                        {request.item_service_requested}
                      </p>
                    </div>

                    {/* View Button */}
                    {/* View Button */}
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowViewModal(true);
                      }}
                      className="w-full mb-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center space-x-2 text-sm font-medium shadow-md transition-all"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View Details</span>
                    </button>

                    {/* Action Buttons for assigned manager */}
                    {isAssignedManager(request) && (
                      <div className="pt-2 border-t-2 border-gray-200 space-y-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setApprovalData({
                              action: "approve",
                              comments: "",
                            });
                            setShowApprovalModal(true);
                          }}
                          disabled={processingId === request.request_id}
                          className="w-full px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-bold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-sm transition-all"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setApprovalData({
                              action: "reject",
                              comments: "",
                            });
                            setShowApprovalModal(true);
                          }}
                          disabled={processingId === request.request_id}
                          className="w-full px-3 py-2 border-2 border-red-300 text-red-700 rounded-lg text-sm font-bold hover:bg-red-50 disabled:opacity-50 transition-all"
                        >
                          ✕ Reject
                        </button>
                      </div>
                    )}

                    {!isAssignedManager(request) && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 italic text-center">
                          Assigned to:{" "}
                          {request.ApprovingManager?.name || "Another manager"}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                {groups[priority].length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No {config.label.toLowerCase()} priority requests
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="space-y-6">
        {pendingRequests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="text-gray-500">
              <p className="text-lg font-medium">No pending approvals</p>
              <p className="text-sm mt-1">All requests have been processed</p>
            </div>
          </div>
        ) : (
          pendingRequests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg shadow-md border border-gray-200"
            >
              {/* Request Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Request {request.request_id}
                      </h3>
                      {isAssignedManager(request) && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          Assigned to you
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Submitted on{" "}
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(request.amount)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Next: {getNextStage(request.amount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Requester Info */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Requester Information
                    </h4>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Name:</span>{" "}
                        {request.requester_name}
                      </p>
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Email:</span>{" "}
                        {request.requester_email}
                      </p>
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Department:</span>{" "}
                        {purchaseRequestService.formatDepartment(
                          request.department,
                        )}
                      </p>
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Priority:</span>
                        <span
                          className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                            request.priority === "urgent"
                              ? "bg-red-100 text-red-800"
                              : request.priority === "high"
                                ? "bg-orange-100 text-orange-800"
                                : request.priority === "medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                          }`}
                        >
                          {request.priority.toUpperCase()}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Vendor Info */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Vendor Information
                    </h4>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Vendor:</span>{" "}
                        {request.vendor_name}
                      </p>
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Amount:</span>{" "}
                        {formatCurrency(request.amount)}
                      </p>
                      {parseFloat(request.amount) >= getThreshold() && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                          <p className="text-xs text-blue-800">
                            ⚠️ This request requires procurement review due to
                            amount ≥ {formatCurrency(getThreshold())}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Item Description */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Item/Service Requested
                  </h4>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                    {request.item_service_requested}
                  </p>
                </div>

                {/* Bank Details */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Vendor Bank Details
                  </h4>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border whitespace-pre-wrap">
                    {request.vendor_bank_details}
                  </p>
                </div>

                {/* Notes */}
                {request.notes && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </h4>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                      {request.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <Link
                    to={`/purchase-requests/${request.request_id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Full Details
                  </Link>

                  {renderActionButtons(request)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderDashboardView = () => {
    const stats = getDashboardStats();

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600 mb-1">Total Pending</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(stats.totalValue)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <p className="text-sm text-gray-600 mb-1">Assigned to You</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats.myRequests}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(stats.myValue)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <p className="text-sm text-gray-600 mb-1">Require Procurement</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats.requireProcurement}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              ≥ {formatCurrency(getThreshold())}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <p className="text-sm text-gray-600 mb-1">Departments</p>
            <p className="text-3xl font-bold text-gray-900">
              {Object.keys(stats.byDepartment).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Active departments</p>
          </div>
        </div>

        {/* Department Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Requests by Department
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.byDepartment)
              .sort((a, b) => b[1].count - a[1].count)
              .map(([dept, data]) => (
                <div
                  key={dept}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {purchaseRequestService.formatDepartment(dept)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {data.count} request{data.count !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {formatCurrency(data.value)}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Recent Requests Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Requests
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requester
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingRequests.slice(0, 10).map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/purchase-requests/${request.request_id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {request.request_id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {request.requester_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {purchaseRequestService.formatDepartment(
                        request.department,
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(request.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          request.priority === "urgent"
                            ? "bg-red-100 text-red-800"
                            : request.priority === "high"
                              ? "bg-orange-100 text-orange-800"
                              : request.priority === "medium"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                        }`}
                      >
                        {request.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {isAssignedManager(request) ? (
                        <span className="text-green-600 font-medium">
                          Assigned to you
                        </span>
                      ) : (
                        <span className="text-gray-500">Other manager</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Pending Approvals
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Review and approve purchase requests assigned to you.
            </p>
          </div>
          <button
            onClick={fetchPendingApprovals}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md"
          >
            <RefreshCw size={18} />
            <span className="font-medium">Refresh</span>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 text-white shadow-lg">
            <p className="text-blue-100 text-xs font-medium mb-1">
              Total Pending
            </p>
            <p className="text-2xl font-bold">{pendingRequests.length}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 text-white shadow-lg">
            <p className="text-green-100 text-xs font-medium mb-1">
              Assigned to You
            </p>
            <p className="text-2xl font-bold">
              {pendingRequests.filter((r) => isAssignedManager(r)).length}
            </p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-3 text-white shadow-lg">
            <p className="text-orange-100 text-xs font-medium mb-1">
              High Priority
            </p>
            <p className="text-2xl font-bold">
              {
                pendingRequests.filter(
                  (r) => r.priority === "urgent" || r.priority === "high",
                ).length
              }
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 text-white shadow-lg">
            <p className="text-purple-100 text-xs font-medium mb-1">
              Total Value
            </p>
            <p className="text-xl font-bold">
              {formatCurrency(
                pendingRequests.reduce(
                  (sum, r) => sum + parseFloat(r.amount || 0),
                  0,
                ),
              )}
            </p>
          </div>
        </div>
      </div>

      {/* View Content */}
      {renderKanbanView()}

      {/* View Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-7xl w-full h-[90vh] overflow-hidden shadow-2xl transform transition-all flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 text-white relative overflow-hidden flex-shrink-0">
              <div className="absolute inset-0 bg-black opacity-10"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Approval Request</h3>
                    <p className="text-indigo-100 text-sm font-medium">
                      {selectedRequest.request_id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedRequest(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-gray-50 to-indigo-50/30">
              {/* Amount Banner */}
              <div className="mb-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-4 text-white shadow-lg flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs font-medium mb-1">
                    Total Amount
                  </p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(selectedRequest.amount)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-100 text-xs font-medium mb-1">
                    Next Stage
                  </p>
                  <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg">
                    <p className="text-sm font-bold">
                      {getNextStage(selectedRequest.amount)}
                    </p>
                  </div>
                </div>
              </div>
              {parseFloat(selectedRequest.amount) >= getThreshold() && (
                <div className="mb-4 bg-yellow-400 text-yellow-900 rounded-lg p-2 flex items-center gap-2">
                  <span className="text-lg">⚠️</span>
                  <p className="text-xs font-semibold">
                    Requires procurement review (≥{" "}
                    {formatCurrency(getThreshold())})
                  </p>
                </div>
              )}

              {/* Main Info Grid - Compact */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                {/* Requester Card */}
                <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-2 shadow">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">
                      Requester
                    </h4>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="text-gray-500">Name</p>
                      <p className="font-semibold text-gray-900">
                        {selectedRequest.requester_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Department</p>
                      <p className="font-semibold text-gray-900">
                        {purchaseRequestService.formatDepartment(
                          selectedRequest.department,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Priority</p>
                      <span
                        className={`inline-block px-2 py-1 rounded-lg text-xs font-bold ${
                          selectedRequest.priority === "urgent"
                            ? "bg-red-100 text-red-700"
                            : selectedRequest.priority === "high"
                              ? "bg-orange-100 text-orange-700"
                              : selectedRequest.priority === "medium"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                        }`}
                      >
                        {selectedRequest.priority.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Vendor Card */}
                <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg mr-2 shadow">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">Vendor</h4>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="text-gray-500">Name</p>
                      <p className="font-semibold text-gray-900">
                        {selectedRequest.vendor_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Bank Details</p>
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 max-h-20 overflow-y-auto">
                        <p className="text-xs text-gray-900 whitespace-pre-wrap font-mono">
                          {selectedRequest.vendor_bank_details}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item Description Card */}
                <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg mr-2 shadow">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">
                      Item/Service
                    </h4>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-2 border border-purple-200 max-h-24 overflow-y-auto">
                    <p className="text-xs text-gray-900 leading-relaxed">
                      {selectedRequest.item_service_requested}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes & Document - Compact Row */}
              {(selectedRequest.notes ||
                selectedRequest.cdn_url ||
                selectedRequest.supporting_document_path) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {selectedRequest.notes && (
                    <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                      <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center">
                        <span className="mr-2">💬</span> Notes
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 max-h-16 overflow-y-auto">
                        <p className="text-xs text-gray-900">
                          {selectedRequest.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  {(selectedRequest.cdn_url ||
                    selectedRequest.supporting_document_path) && (
                    <div className="bg-white rounded-xl p-3 border border-amber-100 shadow-sm">
                      <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center">
                        <FileText className="h-3 w-3 mr-2 text-amber-600" />{" "}
                        Document
                      </h4>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600">Attached file</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowDocumentViewer(true)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                          >
                            View
                          </button>
                          <a
                            href={
                              selectedRequest.cdn_url ||
                              selectedRequest.supporting_document_path
                            }
                            download
                            className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons - Fixed Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-3 border-t-2 border-gray-200 flex justify-between items-center flex-shrink-0">
              <button
                onClick={() => handleDownloadReceipt(selectedRequest)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all shadow text-sm font-medium"
              >
                <Download className="h-4 w-4" />
                <span>Receipt</span>
              </button>

              {isAssignedManager(selectedRequest) ? (
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setApprovalData({ action: "reject", comments: "" });
                      setShowViewModal(false);
                      setShowApprovalModal(true);
                    }}
                    disabled={processingId === selectedRequest.request_id}
                    className="px-6 py-2 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-all font-bold text-sm"
                  >
                    ✕ Reject
                  </button>

                  <button
                    onClick={() => {
                      setApprovalData({ action: "approve", comments: "" });
                      setShowViewModal(false);
                      setShowApprovalModal(true);
                    }}
                    disabled={processingId === selectedRequest.request_id}
                    className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all font-bold shadow text-sm"
                  >
                    ✓ Approve
                  </button>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                  <p className="text-xs text-amber-800 font-medium">
                    📌 Assigned to:{" "}
                    {selectedRequest.ApprovingManager?.name ||
                      "Another manager"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {approvalData.action === "approve"
                  ? "Confirm Request"
                  : "Reject Request"}
              </h3>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setApprovalData({ action: "", comments: "" });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-md">
              <h4 className="font-medium text-gray-900">Request Summary</h4>
              <p className="text-sm text-gray-600 mt-1">
                <strong>ID:</strong> {selectedRequest.request_id}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Requester:</strong> {selectedRequest.requester_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Amount:</strong>{" "}
                {formatCurrency(selectedRequest.amount)}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Next Stage:</strong>{" "}
                {getNextStage(selectedRequest.amount)}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {approvalData.action === "approve"
                    ? "Approval Comments"
                    : "Rejection Reason"}
                </label>
                <textarea
                  value={approvalData.comments}
                  onChange={(e) =>
                    setApprovalData((prev) => ({
                      ...prev,
                      comments: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    approvalData.action === "approve"
                      ? "Enter approval comments (optional)..."
                      : "Enter reason for rejection (required)..."
                  }
                />
              </div>

              {approvalData.action === "approve" && (
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-sm text-green-800">
                    <strong>✓ Approving this request will:</strong>
                  </p>
                  <ul className="text-sm text-green-700 mt-1 ml-4 list-disc">
                    <li>Move to {getNextStage(selectedRequest.amount)}</li>
                    <li>Send notification to next approver</li>
                    <li>Update request status automatically</li>
                  </ul>
                </div>
              )}

              {approvalData.action === "reject" && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">
                    <strong>⚠️ Rejecting this request will:</strong>
                  </p>
                  <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                    <li>Stop the approval workflow</li>
                    <li>Notify the requester of rejection</li>
                    <li>Require a new request to be submitted</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setApprovalData({ action: "", comments: "" });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (
                    approvalData.action === "reject" &&
                    !approvalData.comments.trim()
                  ) {
                    toast.warning("Please provide a reason for rejection");
                    return;
                  }
                  handleApproval(
                    selectedRequest.request_id,
                    approvalData.action,
                    approvalData.comments,
                  );
                }}
                disabled={processingId === selectedRequest.request_id}
                className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  approvalData.action === "approve"
                    ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                    : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                }`}
              >
                {processingId === selectedRequest.request_id
                  ? "Processing..."
                  : approvalData.action === "approve"
                    ? "Confirm Request"
                    : "Reject Request"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Purchase Receipt Modal */}
      {showReceipt && selectedRequest && (
        <PurchaseReceipt
          request={selectedRequest}
          onClose={() => {
            setShowReceipt(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {/* Document Viewer Modal */}
      {showDocumentViewer && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Supporting Document</h3>
              <button
                onClick={() => setShowDocumentViewer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedRequest.cdn_url || selectedRequest.supporting_document_path)}&embedded=true`}
                className="w-full h-full"
                title="Supporting Document"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerApproval;
